// src/context/SubscriptionContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

export const DEFAULT_FALLBACK_PLANS = [
  {
    id: 1,
    name: "Starter",
    slug: "starter",
    tagline: "Essential construction management for small teams & subcontractors",
    price: 2999,
    annual_price: 29990,
    currency: "INR",
    popular: false,
    features: [
      "Up to 5 Team Members",
      "Up to 3 Active Projects",
      "Project & Task Scheduling",
      "Material & Inventory Tracking",
      "Daily Site Logs",
      "Standard Email Support"
    ]
  },
  {
    id: 2,
    name: "Professional",
    slug: "professional",
    tagline: "Full-featured ERP solution for growing general contractors & developers",
    price: 7999,
    annual_price: 79990,
    currency: "INR",
    popular: true,
    features: [
      "Up to 25 Team Members",
      "Up to 15 Active Projects",
      "Full Finance & Accounting Suite",
      "HR, Workforce & Site Payroll",
      "CRM & Lead Pipeline",
      "Vendor & Subcontractor Portal",
      "Budget vs Actual Variance",
      "Priority 24/7 Phone & Email Support"
    ]
  },
  {
    id: 3,
    name: "Enterprise",
    slug: "enterprise",
    tagline: "Unlimited scale, custom workflows, and dedicated account management",
    price: 19999,
    annual_price: 199990,
    currency: "INR",
    popular: false,
    features: [
      "Unlimited Users & Site Crew",
      "Unlimited Projects & Warehouses",
      "All ERP Modules & Features",
      "Advanced BI Reporting & Analytics",
      "Custom Field Configuration",
      "Automated Daily DB Backups",
      "Dedicated Technical Account Manager",
      "99.9% Uptime SLA Guarantee"
    ]
  }
];

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(DEFAULT_FALLBACK_PLANS);
  const [loading, setLoading] = useState(true);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState(null);

  // Fetch available plans
  const fetchPlans = useCallback(async () => {
    try {
      const res = await api.get('/subscriptions/plans');
      const list = Array.isArray(res.data) 
        ? res.data 
        : (Array.isArray(res.data?.plans) ? res.data.plans : null);
      if (list && list.length > 0) {
        setPlans(list);
      } else {
        setPlans(DEFAULT_FALLBACK_PLANS);
      }
    } catch (err) {
      console.warn('Could not fetch subscription plans, falling back to default:', err.message);
      setPlans(DEFAULT_FALLBACK_PLANS);
    }
  }, []);

  // Fetch current user's subscription
  const fetchCurrentSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    // Super Admin is never blocked
    if (user.role === 'Super Admin' || user.email === 'admin@example.com' || user.email === 'admin@cms.com') {
      const superAdminSub = {
        status: "active",
        plan_id: 3,
        plan_name: "Enterprise",
        days_remaining: 365,
        is_expired: false,
        is_super_admin: true,
        trial_ends_at: new Date(Date.now() + 365 * 86400000).toISOString()
      };
      setSubscription(superAdminSub);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await api.get('/subscriptions/current');
      const data = res.data;
      setSubscription(data);

      // If expired and not Super Admin, trigger the paywall automatically
      if (data.is_expired) {
        setIsPaywallOpen(true);
      }
    } catch (err) {
      // In offline/mock fallback, compute from user object
      const storedSub = user.subscription || {
        status: 'trialing',
        plan_id: 2,
        plan_name: 'Professional',
        days_remaining: 29,
        is_expired: false,
        is_super_admin: false,
        trial_ends_at: new Date(Date.now() + 29 * 86400000).toISOString()
      };
      setSubscription(storedSub);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
  }, [fetchPlans, fetchCurrentSubscription]);

  // Listen for global 402 HTTP event from axiosConfig
  useEffect(() => {
    const handleSubscriptionExpired = (event) => {
      // Super Admin is immune
      if (user?.role === 'Super Admin') return;

      console.warn('🔒 Subscription expired event intercepted:', event.detail);
      setSubscription(prev => ({
        ...(prev || {}),
        status: 'expired',
        is_expired: true,
        days_remaining: 0
      }));
      setIsPaywallOpen(true);
    };

    window.addEventListener('subscription-expired', handleSubscriptionExpired);
    return () => {
      window.removeEventListener('subscription-expired', handleSubscriptionExpired);
    };
  }, [user]);

  // Open checkout modal for a plan
  const initiateCheckout = (plan, billingCycle = 'monthly') => {
    setSelectedPlanForCheckout({ ...plan, billingCycle });
    setIsPaywallOpen(false); // Close paywall to open checkout
    setIsCheckoutOpen(true);
  };

  // Complete & verify payment (simulated or live gateway)
  const completePayment = async (paymentDetails) => {
    try {
      const res = await api.post('/subscriptions/verify-payment', paymentDetails);
      if (res.data?.success) {
        toast.success(res.data.message || 'Subscription successfully activated!');
        setSubscription(res.data.subscription);
        setIsCheckoutOpen(false);
        setIsPaywallOpen(false);
        // Refresh session
        await fetchCurrentSubscription();
        return { success: true };
      }
      return { success: false, message: 'Payment verification failed' };
    } catch (error) {
      console.error('Payment activation error:', error);
      toast.error(error.response?.data?.message || 'Payment processing failed');
      return { success: false, message: error.message };
    }
  };

  const value = {
    subscription,
    plans,
    loading,
    isPaywallOpen,
    isCheckoutOpen,
    selectedPlanForCheckout,
    openPaywall: () => setIsPaywallOpen(true),
    closePaywall: () => {
      // Only allow closing if not expired or if user is super admin
      if (!subscription?.is_expired || subscription?.is_super_admin) {
        setIsPaywallOpen(false);
      } else {
        toast.warning('Please choose a plan to continue accessing BriqERP.');
      }
    },
    openCheckout: initiateCheckout,
    closeCheckout: () => setIsCheckoutOpen(false),
    refreshSubscription: fetchCurrentSubscription,
    completePayment
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
